import { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { BY_SYMBOL, elementColor } from '../elements';
import { parseFormula, type AtomCount } from '../formula';
import { parseEquation } from '../reactions/equation';
import {
  VizCard,
  NowYouTry,
  VIZ_BTN_GHOST,
  DARK_INK,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// ReactionParticles — a particle-level before→after view of a chemical reaction
// that makes CONSERVATION OF ATOMS (and therefore mass) visible: the left box
// holds the reactant molecules drawn atom-by-atom, the right box holds the
// product molecules, and an arrow sits between. The same atoms appear on both
// sides — they only rearrange. A scrub (cue.progress) tweens every reactant atom
// to its matching product position so the learner literally watches the bonds
// break and re-form with nothing created or destroyed.
//
// config: {
//   reactants?: [{ formula, count }],   // e.g. [{formula:'H2',count:2},{formula:'O2',count:1}]
//   products?:  [{ formula, count }],
//   equation?:  string,                 // parsed if reactants/products absent, e.g. "2 H2 + O2 -> 2 H2O"
//   title?: string,
// }
// Default reaction: 2 H2 + O2 -> 2 H2O.
//
// Cue:
//   cue.step      0 = reactants · 1 = rearranging (mid-morph) · 2 = products
//   cue.progress  0..1 scrubs the reactant→product transition continuously
//   cue.play      replays the morph from the start
//   cue.highlight an element symbol to spotlight (others dim)

export interface ReactionParticlesConfig {
  title?: string;
  reactants?: Array<{ formula?: string; count?: number }>;
  products?: Array<{ formula?: string; count?: number }>;
  equation?: string;
  cue_steps?: unknown;
}

interface Props {
  config?: ReactionParticlesConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// ── Geometry ────────────────────────────────────────────────────────────────
const W = 360;
const H = 200;
const BOX_W = 150; // each side box width
const ARROW_W = W - BOX_W * 2; // central gap holding the arrow
const PAD = 12; // inner padding of a box
const R = 8; // atom radius
const BOND = R * 1.85; // satellite distance from a molecule's center

// One drawable atom: an absolute (x,y) plus its element + a stable identity so a
// reactant atom can be tweened to the product atom that shares its identity.
interface Atom {
  symbol: string;
  x: number;
  y: number;
}

// A molecule = a center atom plus bonded satellites, laid out around (cx,cy).
interface Molecule {
  atoms: Atom[]; // [center, ...satellites]
}

// Build the per-side molecule list from formula+count entries.
interface SpeciesEntry {
  formula: string;
  count: number;
  atoms: AtomCount[];
}

function speciesFromEntries(
  entries: Array<{ formula?: string; count?: number }> | undefined,
): SpeciesEntry[] {
  if (!entries || !entries.length) return [];
  return entries
    .map((e) => {
      const formula = (e.formula ?? '').trim();
      const atoms = parseFormula(formula);
      return { formula, count: Math.max(1, Math.round(e.count ?? 1)), atoms };
    })
    .filter((s) => s.atoms.length > 0);
}

// Pick the central atom for a molecule glyph: the least-abundant heavy atom reads
// best as a hub (e.g. O in H2O, C in CO2). Falls back to the first listed atom.
function centralSymbol(atoms: AtomCount[]): string {
  if (!atoms.length) return 'X';
  // Prefer a single-count atom (the natural hub); else the first.
  const single = atoms.find((a) => a.count === 1);
  return (single ?? atoms[0]).symbol;
}

// Flatten a species (one molecule unit) into an ordered atom-symbol list with the
// hub first so layout is deterministic.
function unitSymbols(atoms: AtomCount[]): { center: string; satellites: string[] } {
  const center = centralSymbol(atoms);
  const satellites: string[] = [];
  let centerTaken = false;
  for (const a of atoms) {
    for (let i = 0; i < a.count; i++) {
      if (!centerTaken && a.symbol === center) {
        centerTaken = true;
        continue; // this instance becomes the hub
      }
      satellites.push(a.symbol);
    }
  }
  return { center, satellites };
}

// Lay molecules of a side out on a tidy grid within a box anchored at (boxX, 0).
function layoutSide(species: SpeciesEntry[], boxX: number): Molecule[] {
  // Expand to individual molecule instances.
  const units: Array<{ center: string; satellites: string[] }> = [];
  for (const sp of species) {
    const u = unitSymbols(sp.atoms);
    for (let i = 0; i < sp.count; i++) units.push(u);
  }
  if (!units.length) return [];

  const innerW = BOX_W - PAD * 2;
  const innerH = H - PAD * 2;
  const cols = Math.min(units.length, units.length <= 2 ? units.length : Math.ceil(Math.sqrt(units.length)));
  const rows = Math.ceil(units.length / cols);
  const cellW = innerW / cols;
  const cellH = innerH / rows;

  return units.map((u, i) => {
    const c = i % cols;
    const r = Math.floor(i / cols);
    const cx = boxX + PAD + cellW * (c + 0.5);
    const cy = PAD + cellH * (r + 0.5);
    const atoms: Atom[] = [{ symbol: u.center, x: cx, y: cy }];
    u.satellites.forEach((sym, k) => {
      const a = (k / Math.max(1, u.satellites.length)) * Math.PI * 2 - Math.PI / 2;
      atoms.push({ symbol: sym, x: cx + Math.cos(a) * BOND, y: cy + Math.sin(a) * BOND });
    });
    return { atoms };
  });
}

// Flatten molecules to a single atom list (for tweening + conservation tally).
function flatAtoms(mols: Molecule[]): Atom[] {
  return mols.flatMap((m) => m.atoms);
}

// Per-element totals across an atom list (for the conservation badges).
function tallyAtoms(atoms: Atom[]): Record<string, number> {
  const t: Record<string, number> = {};
  for (const a of atoms) t[a.symbol] = (t[a.symbol] ?? 0) + 1;
  return t;
}

// Pair each reactant atom with a product atom OF THE SAME ELEMENT so the morph
// only moves atoms, never invents them. Returns aligned arrays; if counts differ
// (unbalanced input) the surplus simply has no partner and stays put / fades.
function pairAtoms(from: Atom[], to: Atom[]): Array<{ from: Atom; to: Atom | null }> {
  const byElem: Record<string, Atom[]> = {};
  for (const a of to) (byElem[a.symbol] ??= []).push(a);
  return from.map((a) => {
    const pool = byElem[a.symbol];
    const partner = pool && pool.length ? pool.shift()! : null;
    return { from: a, to: partner };
  });
}

const DEFAULT_REACTANTS = [
  { formula: 'H2', count: 2 },
  { formula: 'O2', count: 1 },
];
const DEFAULT_PRODUCTS = [{ formula: 'H2O', count: 2 }];

export default function ReactionParticles({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  // Resolve reactants/products: explicit config wins, else parse the equation,
  // else fall back to the default water-formation reaction. Coefficients written
  // inline in the equation (e.g. "2 H2") become per-species counts.
  const { reactants, products } = useMemo(() => {
    if (config.reactants?.length || config.products?.length) {
      return {
        reactants: speciesFromEntries(config.reactants ?? DEFAULT_REACTANTS),
        products: speciesFromEntries(config.products ?? DEFAULT_PRODUCTS),
      };
    }
    if (config.equation) {
      const parsed = parseEquation(config.equation);
      if (parsed && parsed.reactants.length && parsed.products.length) {
        const coeffs = extractCoeffs(config.equation);
        const r = parsed.reactants.map((s, i) => ({ formula: s.formula, count: coeffs.react[i] ?? 1, atoms: s.atoms }));
        const p = parsed.products.map((s, i) => ({ formula: s.formula, count: coeffs.prod[i] ?? 1, atoms: s.atoms }));
        return { reactants: r, products: p };
      }
    }
    return {
      reactants: speciesFromEntries(DEFAULT_REACTANTS),
      products: speciesFromEntries(DEFAULT_PRODUCTS),
    };
  }, [config.reactants, config.products, config.equation]);

  // Resolve to a non-empty pair (never render an empty stage).
  const reactSpecies = reactants.length ? reactants : speciesFromEntries(DEFAULT_REACTANTS);
  const prodSpecies = products.length ? products : speciesFromEntries(DEFAULT_PRODUCTS);

  const reactMols = useMemo(() => layoutSide(reactSpecies, 0), [reactSpecies]);
  const prodMols = useMemo(() => layoutSide(prodSpecies, BOX_W + ARROW_W), [prodSpecies]);

  const reactFlat = useMemo(() => flatAtoms(reactMols), [reactMols]);
  const prodFlat = useMemo(() => flatAtoms(prodMols), [prodMols]);
  const pairs = useMemo(() => pairAtoms(reactFlat, prodFlat), [reactFlat, prodFlat]);

  // Conservation tally: atoms per element on each side. Equal counts (balanced)
  // get a check; a mismatch (unbalanced config) is flagged honestly.
  const reactTally = useMemo(() => tallyAtoms(reactFlat), [reactFlat]);
  const prodTally = useMemo(() => tallyAtoms(prodFlat), [prodFlat]);
  const elements = useMemo(() => {
    const seen: string[] = [];
    for (const s of [...reactFlat, ...prodFlat]) if (!seen.includes(s.symbol)) seen.push(s.symbol);
    return seen;
  }, [reactFlat, prodFlat]);
  const conserved = elements.every((el) => (reactTally[el] ?? 0) === (prodTally[el] ?? 0));

  // t = 0 (reactants) … 1 (products); drives the morph. Bonds + the central
  // arrow brighten around the midpoint where bonds break & re-form.
  const [t, setT] = useState(reduced ? 1 : 0);
  const [showTry, setShowTry] = useState(false);
  const interactedRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  const markInteracted = () => {
    setShowTry(false);
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'reaction_particles', conserved, elements });
    }
  };

  // Animate t from 0→1 over `dur` ms (used by self-demo and Replay).
  const runMorph = (dur = 2200, onDone?: () => void) => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    if (reduced) {
      setT(1);
      onDone?.();
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / dur);
      // ease-in-out so the rearrangement feels deliberate
      const eased = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      setT(eased);
      if (k < 1) rafRef.current = requestAnimationFrame(tick);
      else {
        rafRef.current = null;
        onDone?.();
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  // Self-demo on mount: hold reactants briefly, morph to products, then offer
  // "Now you try". Skipped under reduced motion or when a cue is driving.
  useEffect(() => {
    if (reduced || hasCue) {
      setT(1);
      return;
    }
    setT(0);
    const hold = window.setTimeout(() => runMorph(2200, () => setShowTry(true)), 650);
    return () => {
      window.clearTimeout(hold);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
    // Arm once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cue: progress scrubs t directly; step snaps to a phase; play replays.
  useEffect(() => {
    if (!cue) return;
    setShowTry(false);
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (typeof cue.progress === 'number') {
      setT(Math.max(0, Math.min(1, cue.progress)));
    } else if (typeof cue.step === 'number') {
      const phase = ((Math.round(cue.step) % 3) + 3) % 3; // 0,1,2
      setT(phase === 0 ? 0 : phase === 1 ? 0.5 : 1);
    } else if (cue.play) {
      runMorph(2200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue]);

  const replay = () => {
    markInteracted();
    runMorph(2200, () => setShowTry(false));
  };

  // Spotlight: which element(s) the narration is pointing at (others dim).
  const spotlight = cueHighlights(cue);
  const isLit = (sym: string) => spotlight.length === 0 || spotlight.includes(sym.toLowerCase());

  // Bond visibility: reactant bonds fade out as t→0.5, product bonds fade in
  // after. The midpoint is the "loose atoms" moment.
  const reactBondOpacity = Math.max(0, 1 - t / 0.5);
  const prodBondOpacity = Math.max(0, (t - 0.5) / 0.5);
  // Arrow energy glows around the midpoint.
  const arrowGlow = 1 - Math.abs(t - 0.5) * 2;

  const phaseLabel = t <= 0.02 ? 'Reactants' : t >= 0.98 ? 'Products' : 'Rearranging — bonds breaking & re-forming';

  const eqText = useMemo(
    () => `${reactSpecies.map((s) => coeffLabel(s.count) + prettyFormula(s.formula)).join('  +  ')}  →  ${prodSpecies
      .map((s) => coeffLabel(s.count) + prettyFormula(s.formula))
      .join('  +  ')}`,
    [reactSpecies, prodSpecies],
  );

  return (
    <VizCard
      eyebrow="Chemical reactions · Conservation of mass"
      title={config.title ?? 'Atoms rearrange — nothing is created or destroyed'}
      subtitle={<span className="font-mono">{eqText}</span>}
      reducedMotion={reduced}
      hint="The same atoms appear before and after — they only swap bonding partners"
      actions={
        <button type="button" onClick={replay} className={VIZ_BTN_GHOST} aria-label="Replay the reaction">
          <RotateCcw size={12} /> Replay
        </button>
      }
    >
      <div className="relative w-full">
        <NowYouTry show={showTry && !reduced && !hasCue} />

        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full rounded-md border border-ws-border bg-ws-surface-2"
          style={{ maxHeight: 240 }}
          role="img"
          aria-label="Particle-level view of a chemical reaction showing conservation of atoms"
        >
          {/* the two reaction boxes */}
          <rect x={1} y={1} width={BOX_W - 2} height={H - 2} rx={8} fill="none" stroke="var(--ws-border)" strokeDasharray="4 4" />
          <rect x={BOX_W + ARROW_W + 1} y={1} width={BOX_W - 2} height={H - 2} rx={8} fill="none" stroke="var(--ws-border)" strokeDasharray="4 4" />

          {/* box captions */}
          <text x={BOX_W / 2} y={16} textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--ws-ink-3)" letterSpacing="0.5">
            REACTANTS
          </text>
          <text x={BOX_W + ARROW_W + BOX_W / 2} y={16} textAnchor="middle" fontSize="9" fontWeight="700" fill="var(--ws-ink-3)" letterSpacing="0.5">
            PRODUCTS
          </text>

          {/* the yields arrow, glowing as bonds rearrange */}
          {(() => {
            const ay = H / 2;
            const ax1 = BOX_W + 14;
            const ax2 = BOX_W + ARROW_W - 14;
            return (
              <g>
                <line
                  x1={ax1}
                  y1={ay}
                  x2={ax2}
                  y2={ay}
                  stroke="var(--ws-accent)"
                  strokeWidth={2 + arrowGlow * 2}
                  opacity={0.45 + arrowGlow * 0.55}
                />
                <polygon
                  points={`${ax2},${ay - 5} ${ax2 + 8},${ay} ${ax2},${ay + 5}`}
                  fill="var(--ws-accent)"
                  opacity={0.45 + arrowGlow * 0.55}
                />
              </g>
            );
          })()}

          {/* reactant-side bonds (fade out as atoms separate) */}
          {reactBondOpacity > 0.01 &&
            reactMols.map((m, mi) =>
              m.atoms.slice(1).map((sat, si) => (
                <line
                  key={`rb-${mi}-${si}`}
                  x1={m.atoms[0].x}
                  y1={m.atoms[0].y}
                  x2={sat.x}
                  y2={sat.y}
                  stroke="var(--ws-ink-4)"
                  strokeWidth={2}
                  opacity={reactBondOpacity}
                />
              )),
            )}

          {/* product-side bonds (fade in once atoms arrive) */}
          {prodBondOpacity > 0.01 &&
            prodMols.map((m, mi) =>
              m.atoms.slice(1).map((sat, si) => (
                <line
                  key={`pb-${mi}-${si}`}
                  x1={m.atoms[0].x}
                  y1={m.atoms[0].y}
                  x2={sat.x}
                  y2={sat.y}
                  stroke="var(--ws-ink-4)"
                  strokeWidth={2}
                  opacity={prodBondOpacity}
                />
              )),
            )}

          {/* the conserved atoms: each tweens from its reactant slot to a matching
              product slot. Atoms with no partner (unbalanced input) stay put. */}
          {pairs.map((pr, i) => {
            const to = pr.to ?? pr.from;
            const x = pr.from.x + (to.x - pr.from.x) * t;
            const y = pr.from.y + (to.y - pr.from.y) * t;
            const lit = isLit(pr.from.symbol);
            return (
              <g key={`atom-${i}`} opacity={lit ? 1 : 0.25} style={{ transition: reduced ? undefined : 'opacity 200ms' }}>
                <circle cx={x} cy={y} r={R} fill={elementColor(pr.from.symbol)} stroke="rgba(0,0,0,0.28)" strokeWidth={1} />
                <text x={x} y={y + 3} textAnchor="middle" fontSize="8" fontWeight="700" fill={DARK_INK}>
                  {pr.from.symbol}
                </text>
              </g>
            );
          })}
          {/* any product atoms left unpaired (more atoms on the right) appear as
              they arrive so the right box is never under-drawn */}
          {prodFlat.length > pairs.length &&
            prodFlat.slice(pairs.length).map((a, i) => {
              const lit = isLit(a.symbol);
              return (
                <g key={`extra-${i}`} opacity={(lit ? 1 : 0.25) * t}>
                  <circle cx={a.x} cy={a.y} r={R} fill={elementColor(a.symbol)} stroke="rgba(0,0,0,0.28)" strokeWidth={1} />
                  <text x={a.x} y={a.y + 3} textAnchor="middle" fontSize="8" fontWeight="700" fill={DARK_INK}>
                    {a.symbol}
                  </text>
                </g>
              );
            })}
        </svg>

        {/* phase label + scrubber */}
        <div className="mt-2 flex items-center gap-3">
          <span className="min-w-0 flex-shrink-0 text-[11px] font-medium text-ws-ink-2">{phaseLabel}</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={t}
            onChange={(e) => {
              setT(Number(e.target.value));
              markInteracted();
            }}
            aria-label="Scrub the reaction from reactants to products"
            className="h-1 flex-1 cursor-pointer accent-ws-accent"
          />
        </div>

        {/* conservation tally: atoms in = atoms out, element by element */}
        <div className="mt-3 rounded-md border border-ws-border bg-ws-surface px-3 py-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-ws-ink-2">Atom count — both sides</span>
            <span className={`text-[11px] font-semibold ${conserved ? 'text-ws-accent' : 'text-ws-ink-3'}`}>
              {conserved ? 'Balanced — mass conserved' : 'Counts differ'}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {elements.map((el) => {
              const r = reactTally[el] ?? 0;
              const p = prodTally[el] ?? 0;
              const ok = r === p;
              return (
                <span
                  key={el}
                  className="inline-flex items-center gap-1 rounded-sm border border-ws-border px-1.5 py-0.5 text-[11px]"
                  style={{ opacity: isLit(el) ? 1 : 0.4 }}
                  title={`${BY_SYMBOL[el]?.name ?? el}: ${r} in → ${p} out`}
                >
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: elementColor(el), border: '1px solid rgba(0,0,0,0.25)' }}
                  />
                  <span className="font-mono text-ws-ink-2">{el}</span>
                  <span className={ok ? 'text-ws-ink-2' : 'text-ws-ink-3 line-through'}>
                    {r}={p}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </VizCard>
  );
}

// Pull inline coefficients out of an equation string so "2 H2 + O2 -> 2 H2O"
// keeps its 2/1/2 multipliers (parseEquation strips them). Order matches the
// species order parseEquation returns.
function extractCoeffs(equation: string): { react: number[]; prod: number[] } {
  const norm = equation.replace(/⟶|→|-->|->|=>|=/g, '|');
  const [lhs = '', rhs = ''] = norm.split('|');
  const side = (s: string) =>
    s
      .split('+')
      .map((tok) => tok.trim())
      .filter(Boolean)
      .map((tok) => {
        const m = tok.match(/^\s*(\d+)/);
        return m ? parseInt(m[1], 10) : 1;
      });
  return { react: side(lhs), prod: side(rhs) };
}

const coeffLabel = (n: number) => (n > 1 ? `${n} ` : '');

// Render subscripts as small digits (visual only) — mirrors the other widgets.
function prettyFormula(formula: string): string {
  return formula.replace(/(\d+)/g, (m) =>
    m.split('').map((d) => '₀₁₂₃₄₅₆₇₈₉'[Number(d)]).join(''),
  );
}
